/**
 * What to do with the React Query cache when the signed-in identity changes.
 *
 * THE PROBLEM. `GET /users/me` is cached under a constant key with no user
 * identity in it, and login is a client-side `navigate()` rather than a page
 * load, so the cache outlives the identity change. Without an explicit reset,
 * the next person to sign in on this tab is served the PREVIOUS user's cached
 * profile while the refetch is in flight — and the effect in AuthContext that
 * mirrors the profile into `auth.user` and localStorage then writes their
 * `role`, `accountId` and capability fields (`permissionFlags`,
 * `canManagePermissionFlags`, `canOverrideOrderPaidStatus`) into the new
 * session for a render or two. `pages/auth/LogIn` even derives its landing
 * route from `auth.user.role`, so a stale profile can misroute.
 *
 * The server re-derives every permission from the request's own JWT, so this is
 * a display-only leak rather than an escalation. But showing someone
 * permissions they do not hold is not acceptable on the screens that manage
 * permissions.
 *
 * WHY IT IS A MODULE. The fix is two calls inside `AuthContext`, and this repo
 * has no frontend component test infrastructure (jest is `testEnvironment:
 * 'node'`; no testing-library, no jsdom) that could pin them. Extracting the
 * policy makes it testable under the existing runner, and matches the
 * convention stated at `TaxRegistrations/index.tsx:36-37`: logic lives in a
 * tested sibling, components stay wiring.
 *
 * THE DISTINCTION THAT MATTERS. `invalidateQueries` marks an entry stale but
 * KEEPS SERVING IT until the refetch resolves. `removeQueries` deletes it, so
 * there is nothing to serve. Only the second closes the leak — which is why the
 * tests assert `invalidateQueries` is never called, not merely that remove was.
 */

/** The slice of QueryClient this policy needs, so tests inject a plain fake. */
export interface SessionQueryCache {
  clear(): void;
  removeQueries(filters: { queryKey: unknown[] }): void;
}

/**
 * The key AuthContext caches the canonical profile under.
 *
 * Exported and shared with the `useQuery` call so the cached key and the reset
 * cannot drift — renaming one without the other would silently stop clearing.
 */
export const USER_DETAILS_QUERY_KEY = 'userDetails';

/**
 * Signing out: discard everything.
 *
 * Every cached entry was fetched as the departing user — their leads, orders,
 * accounts and permission flags, not just their identity. A page reload would
 * throw all of it away; a sign-out should be no different.
 */
export function resetCacheForSignOut(cache: SessionQueryCache): void {
  cache.clear();
}

/**
 * A new identity has been established (login, or a re-mint after session
 * expiry): drop the cached profile so the incoming user's own is fetched fresh.
 *
 * Scoped rather than a full clear, because this also runs for paths where the
 * identity did not actually change — re-minting a token for the same person
 * should not throw away lists they are still looking at. Sign-out is the event
 * that clears everything.
 */
export function resetCacheForNewIdentity(cache: SessionQueryCache): void {
  cache.removeQueries({ queryKey: [USER_DETAILS_QUERY_KEY] });
}
